import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { useInternalLdapUser } from "../../../../reactQuery/users";
import { internalInviteSchema, schema } from "../../../../schemas/inviteUser";
import {
  inviteExternalUser,
  inviteInternalUser,
} from "../../../../services/apiUser";

function getInviteUserErrorMessage(error) {
  const responseData = error?.response?.data;

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  if (typeof responseData?.message?.message === "string" && responseData.message.message.trim()) {
    return responseData.message.message;
  }

  if (typeof responseData?.error?.message === "string" && responseData.error.message.trim()) {
    return responseData.error.message;
  }

  return "Something went wrong!";
}

export function useInviteUserForm({ onClose }) {
  const [apiError, setApiError] = useState("");
  const [tab, setTab] = useState("externalUser");
  const { data } = useInternalLdapUser();
  const queryClient = useQueryClient();

  const formMethods = useForm({
    resolver: yupResolver(tab === "internalUser" ? internalInviteSchema : schema),
    defaultValues: {
      tab: "externalUser",
    },
  });

  const { handleSubmit, watch } = formMethods;
  const tabValue = watch("tab");

  useEffect(() => {
    setTab(tabValue);
  }, [tabValue]);

  const { mutate, isPending: loading } = useMutation({
    mutationFn: async (payload) => {
      const apiFunc =
        tab === "internalUser" ? inviteInternalUser : inviteExternalUser;

      toast.dismiss();

      return await toast.promise(apiFunc(payload), {
        loading: "Processing...",
        success: (response) =>
          response?.data?.message || "Operation successful!",
        error: (error) => getInviteUserErrorMessage(error),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["LDAPinternalList"],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      onClose();
    },
    onError: (error) => {
      setApiError(getInviteUserErrorMessage(error));
      onClose();
    },
  });

  const submit = useCallback(
    (formData) => {
      setApiError("");

      if (tab === "internalUser") {
        const selected = data?.data?.data?.responseData?.find(
          (_item, index) => index === Number(formData.user)
        );

        if (!selected) {
          return;
        }

        mutate({
          first_name: selected.first_name,
          last_name: selected.last_name,
          email: selected.mail || "null",
        });
        return;
      }

      const newFormData = { ...formData };
      delete newFormData.tab;
      mutate(newFormData);
    },
    [data, mutate, tab]
  );

  const userOptions = useMemo(
    () =>
      data?.data?.data?.responseData.map((item, index) => ({
        label: `${item.first_name} ${item.last_name}`,
        value: String(index),
      })) || [],
    [data]
  );

  return {
    apiError,
    tab,
    loading,
    userOptions,
    formMethods,
    handleSubmit,
    submit,
  };
}
